import { useState } from "react";
import { api, setToken } from "../lib/api";

const bytes = (s) => new TextEncoder().encode(s).length;

export function problem(pw, again, given) {
  if (pw.length < 8) return "At least 8 characters.";
  if (bytes(pw) > 72) return "At most 72 characters.";
  if (pw !== again) return "The two passwords do not match.";
  if (given && pw === given) return "Pick a password that is different from the one you were given.";
  return null;
}

export default function SignIn({ onSignedIn }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [mode, setMode] = useState("plain"); // plain | first | renew
  const [name, setName] = useState("");
  const [given, setGiven] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [keep, setKeep] = useState(true);

  const reset = () => { setMode("plain"); setGiven(""); setAgain(""); setErr(""); };

  const finish = (r) => { setToken(r.data.accessToken); onSignedIn(r.data.me); };

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (mode !== "plain") {
      const p = problem(pw, again, mode === "renew" ? given : null);
      if (p) return setErr(p);
    }
    setBusy(true);
    const r = mode === "first"
      ? await api("/auth/first-password", { method: "POST", auth: false, body: { email, password: pw, confirm: again, keepSignedIn: keep } })
      : await api("/auth/login", { method: "POST", auth: false, body: mode === "renew" ? { email, password: given, newPassword: pw, keepSignedIn: keep } : { email, password: pw, keepSignedIn: keep } });
    setBusy(false);
    if (r.ok) return finish(r);
    const { code, message, details } = r.error;
    if (code === "PASSWORD_NOT_SET") { setMode("first"); setName(details?.name || ""); setPw(""); setAgain(""); return; }
    if (code === "NEW_PASSWORD_REQUIRED") { setMode("renew"); setName(details?.name || ""); setGiven(pw); setPw(""); setAgain(""); return; }
    if (mode === "renew" && code === "BAD_CREDENTIALS") { reset(); setPw(""); }
    setErr(code === "NETWORK" && mode !== "plain" ? "Could not set the password. Try again." : message);
  }

  const setting = mode !== "plain";
  return (
    <main className="signin" data-testid="signin-page">
      <div className="signin-rule" aria-hidden="true" />
      <section className="signin-copy">
        <img src="/logo-160.png" alt="Creative Khichdi" className="signin-logo" data-testid="signin-logo" />
        <div className="hl-bars">
          <div className="hl-bar">from chaos</div>
          <div className="hl-bar hl-indent">comes creativity</div>
        </div>
        <h1 className="stack-head">YOUR DAY,<br /><span className="hl-grey">PACKED.</span></h1>
        <p className="signin-line">Tomorrow's list is packed the evening before, like a tiffin. Hit play on what you are doing. The floor sees it live, and we finally see where the hours go.</p>
      </section>
      <section className="signin-formcol">
        <form onSubmit={submit} className="card-hard" data-testid="signin-form" noValidate>
          <div className="lbl" data-testid="signin-form-label">{setting ? "Set your password" : "Sign in"}</div>
          <label htmlFor="email" className="fld-lbl">Work email</label>
          <input id="email" type="email" autoComplete="email" placeholder="you@creativekhichdi.com" value={email}
            onChange={(e) => { setEmail(e.target.value); if (setting) reset(); }} className="fld" data-testid="signin-email-input" required />
          {mode === "first" && (
            <p className="note" role="status" data-testid="signin-first-note">First sign-in for <b>{name}</b>. Choose your password: at least 8 characters. You will use it every time from now on.</p>
          )}
          {mode === "renew" && (
            <p className="note" role="status" data-testid="signin-renew-note">Choose your own password to go on. <b>{name}</b>, management reset yours: pick a new one, at least 8 characters, that only you know.</p>
          )}
          <label htmlFor="pw" className="fld-lbl">{setting ? "New password" : "Password"}</label>
          <input id="pw" type="password" maxLength={72} autoComplete={setting ? "new-password" : "current-password"} value={pw}
            onChange={(e) => setPw(e.target.value)} className="fld" data-testid="signin-password-input" />
          {setting && (<>
            <label htmlFor="pw2" className="fld-lbl">Type it again</label>
            <input id="pw2" type="password" maxLength={72} autoComplete="new-password" value={again} autoFocus={mode === "first"}
              onChange={(e) => setAgain(e.target.value)} className="fld" data-testid="signin-password-again-input" />
          </>)}
          <label className="check keep" data-testid="signin-keep-label">
            <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} data-testid="signin-keep-checkbox" />
            <span>Keep me signed in<small>{keep ? "For 90 days on this device." : "Until you close the browser. Use this on a shared computer."}</small></span>
          </label>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="signin-submit-button">
            {setting ? "Set password and sign in" : "Sign in"}
          </button>
          {err && <p className="err" role="alert" data-testid="signin-error">{err}</p>}
        </form>
      </section>
    </main>
  );
}
